/**
 * 支持 @ 角色 / 物品 / 场景 三合一 mention 的 textarea。
 *
 * 设计原则（参考 Slack / 飞书）：
 * - 输入 `@` 触发统一候选浮层（混合三类，按 kind 视觉区分）
 * - 上下键 / Tab / Enter 选中插入；Esc 关闭
 * - 文本里只存纯文本 `@张三`，不存 ID，所见即所得
 * - 名字含空格 / 标点等"非合法字符"时使用 `@[名字]` 包裹
 *
 * 候选字符集：[\u4e00-\u9fa5A-Za-z0-9_]（中文 / 英数 / 下划线）。
 *
 * 解析优先级（同名时）：character > prop > scene
 *   场景按"取第一个"语义：extractMentions.sceneId 只返回首次匹配，多余的进 sceneIdsExtra 供 UI 警告。
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

export type MentionKind = 'character' | 'prop' | 'scene';

export interface MentionCandidate {
  id: string;
  name: string;
  kind: MentionKind;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** 候选项（混合 character + prop + scene，按 kind 字段区分） */
  candidates: MentionCandidate[];
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  className?: string;
  /**
   * 禁用 @ 唤起选择器（纯文本输入）。
   * 用于「多图 → 视频」模式下的「视频指导」tab：该模式只描述动作 / 节奏 / 过渡，
   * 不应注入资产引用。注意：仍保留整段删除逻辑不受影响——但禁用时不再弹出候选浮层。
   */
  disableMention?: boolean;
}

const NAME_TAIL_RE_AT = /@([\u4e00-\u9fa5A-Za-z0-9_]*)$/;

/**
 * 命令式 ref 暴露的方法：
 * - `insertText(text)`：把 text 插入到光标位置；若失焦或输入框未挂载，则末尾追加。
 *   插入前若光标左侧已有非空内容且不以中文/英文常见标点结尾，会自动补一个 `，`。
 *   插入后光标停在新片段末尾，并 focus 输入框。
 */
export interface MentionTextareaHandle {
  insertText: (text: string) => void;
}

export const MentionTextarea = forwardRef<MentionTextareaHandle, Props>(function MentionTextarea({
  value,
  onChange,
  candidates,
  placeholder,
  rows = 4,
  maxLength = 2000,
  className,
  disableMention = false,
}, ref) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupQuery, setPopupQuery] = useState('');
  const [popupIndex, setPopupIndex] = useState(0);

  // 把 value 同步到 ref，让命令式 insertText 能拿到最新值（避免闭包旧值）
  const valueRef = useRef(value);
  valueRef.current = value;

  useImperativeHandle(
    ref,
    () => ({
      insertText: (text: string) => {
        if (!text) return;
        const ta = taRef.current;
        const current = valueRef.current;
        const cursor =
          ta && document.activeElement === ta
            ? ta.selectionStart ?? current.length
            : current.length;
        const before = current.slice(0, cursor);
        const after = current.slice(cursor);

        // 自动连接符：左侧已有内容且末尾不是空白 / 中英标点 → 先补「，」
        const TRAIL_PUNCT_RE = /[\s,，。.!?！？;；:：\n]$/;
        const needSeparator = before.length > 0 && !TRAIL_PUNCT_RE.test(before);
        const piece = needSeparator ? `，${text}` : text;

        const next = (before + piece + after).slice(0, maxLength);
        onChange(next);
        setPopupOpen(false);

        requestAnimationFrame(() => {
          const t = taRef.current;
          if (!t) return;
          const newCursor = Math.min((before + piece).length, next.length);
          t.focus();
          t.setSelectionRange(newCursor, newCursor);
        });
      },
    }),
    [onChange, maxLength]
  );

  /** 候选列表：query 过滤后按 character → prop → scene 排序，最多 10 项 */
  const filtered = useMemo(() => {
    const q = popupQuery.trim().toLowerCase();
    const matched = q
      ? candidates.filter((c) => c.name.toLowerCase().includes(q))
      : candidates;
    const order: MentionKind[] = ['character', 'prop', 'scene'];
    return [...matched]
      .sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind))
      .slice(0, 10);
  }, [candidates, popupQuery]);

  useEffect(() => {
    if (!popupOpen) return;
    setPopupIndex(0);
  }, [popupQuery, popupOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value.slice(0, maxLength);
    onChange(next);

    // 禁用 mention 时纯文本输入，永不弹出候选浮层
    if (disableMention) {
      if (popupOpen) setPopupOpen(false);
      return;
    }

    const ta = e.target;
    const cursor = ta.selectionStart ?? next.length;
    const before = next.slice(0, cursor);
    const atMatch = before.match(NAME_TAIL_RE_AT);
    if (atMatch) {
      setPopupQuery(atMatch[1] ?? '');
      setPopupOpen(true);
    } else {
      setPopupOpen(false);
    }
  };

  const insertMention = (cand: MentionCandidate) => {
    const ta = taRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? value.length;
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const m = before.match(NAME_TAIL_RE_AT);
    if (!m) return;
    const startOfTrigger = before.length - m[0].length;
    const needsBrackets = !/^[\u4e00-\u9fa5A-Za-z0-9_]+$/.test(cand.name);
    const inserted = needsBrackets ? `@[${cand.name}] ` : `@${cand.name} `;
    const next = value.slice(0, startOfTrigger) + inserted + after;
    onChange(next.slice(0, maxLength));
    setPopupOpen(false);

    requestAnimationFrame(() => {
      if (!taRef.current) return;
      const newCursor = startOfTrigger + inserted.length;
      taRef.current.setSelectionRange(newCursor, newCursor);
      taRef.current.focus();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 整体删除：光标前紧贴一个 @[...] 或 @<候选名> 时，Backspace 一次删掉整段 mention
    if (e.key === 'Backspace' && !popupOpen) {
      const ta = e.currentTarget;
      const cursor = ta.selectionStart ?? 0;
      const selEnd = ta.selectionEnd ?? cursor;
      if (cursor === selEnd) {
        const before = value.slice(0, cursor);
        // 1) @[名字]
        const bracketMatch = before.match(/@\[([^\]]+)\]\s?$/);
        if (bracketMatch) {
          e.preventDefault();
          const removed = bracketMatch[0].length;
          const next = value.slice(0, cursor - removed) + value.slice(cursor);
          onChange(next);
          requestAnimationFrame(() => {
            if (!taRef.current) return;
            const newCursor = cursor - removed;
            taRef.current.setSelectionRange(newCursor, newCursor);
          });
          return;
        }
        // 2) 裸 @名字 + 可选末尾空格：按候选名最长匹配
        const trimmed = /\s$/.test(before) ? before.slice(0, -1) : before;
        const trailingSpaceLen = before.length - trimmed.length;
        const sorted = [...candidates].sort((a, b) => b.name.length - a.name.length);
        for (const cand of sorted) {
          const needle = `@${cand.name}`;
          if (trimmed.endsWith(needle)) {
            e.preventDefault();
            const removed = needle.length + trailingSpaceLen;
            const next = value.slice(0, cursor - removed) + value.slice(cursor);
            onChange(next);
            requestAnimationFrame(() => {
              if (!taRef.current) return;
              const newCursor = cursor - removed;
              taRef.current.setSelectionRange(newCursor, newCursor);
            });
            return;
          }
        }
      }
    }

    if (!popupOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setPopupOpen(false);
      return;
    }
    if (filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPopupIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPopupIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filtered[popupIndex]);
    }
  };

  // 点击外部关闭
  useEffect(() => {
    if (!popupOpen) return;
    const onDoc = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (popupRef.current?.contains(target)) return;
      if (taRef.current?.contains(target)) return;
      setPopupOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [popupOpen]);

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className={className}
      />
      {popupOpen && (
        <div
          ref={popupRef}
          className="absolute left-0 right-0 top-full z-[200] mt-1 max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/40"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
            <span>{candidates.length === 0 ? '当前项目还没有可引用资产' : '选择资产'}</span>
            <span className="text-[9px] normal-case tracking-normal">
              <KindDot kind="character" />角色 ·{' '}
              <KindDot kind="prop" />物品 ·{' '}
              <KindDot kind="scene" />场景
            </span>
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-500">
              {candidates.length === 0
                ? '请先在左栏「角色」/「物品」/「场景」里加入或新建资产'
                : '没有匹配的项，按 Esc 关闭'}
            </div>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              {filtered.map((c, idx) => (
                <li key={`${c.kind}:${c.id}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setPopupIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(c);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition ${
                      idx === popupIndex
                        ? c.kind === 'character'
                          ? 'bg-sky-50 text-sky-900'
                          : c.kind === 'prop'
                            ? 'bg-amber-50 text-amber-900'
                            : 'bg-emerald-50 text-emerald-900'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold text-white ${
                        c.kind === 'character'
                          ? 'bg-gradient-to-br from-sky-500 to-cyan-400'
                          : c.kind === 'prop'
                            ? 'bg-gradient-to-br from-amber-500 to-orange-400'
                            : 'bg-gradient-to-br from-emerald-500 to-teal-400'
                      }`}
                    >
                      {c.kind === 'character' ? '人' : c.kind === 'prop' ? '物' : '景'}
                    </span>
                    <span className="truncate">{c.name}</span>
                    <span
                      className={`ml-auto rounded px-1.5 py-0.5 text-[10px] ${
                        c.kind === 'character'
                          ? 'bg-sky-100 text-sky-700'
                          : c.kind === 'prop'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {c.kind === 'character' ? '角色' : c.kind === 'prop' ? '物品' : '场景'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

function KindDot({ kind }: { kind: MentionKind }) {
  const cls =
    kind === 'character'
      ? 'bg-sky-500'
      : kind === 'prop'
        ? 'bg-amber-500'
        : 'bg-emerald-500';
  return (
    <span className={`inline-block h-1.5 w-1.5 rounded-full align-middle ${cls} mx-0.5`} />
  );
}

export interface ExtractMentionsResult {
  characterIds: string[];
  propIds: string[];
  /** 场景按"取第一个"语义：仅返回首次 @ 到的场景 ID */
  sceneId: string | null;
  /** 多余被 @ 到但被忽略的场景 ID 列表（用于前端警告） */
  sceneIdsExtra: string[];
}

/**
 * 从 prompt 文本里按 @ 出现顺序解析出已知 ID。
 *
 * 解析优先级（同名时）：character > prop > scene
 * - 角色 / 物品：按出现顺序去重返回
 * - 场景：仅首次 @ 到的进 sceneId，后续多写进 sceneIdsExtra（UI 用于警告）
 */
export function extractMentions(
  prompt: string,
  characterCandidates: Array<{ id: string; name: string }>,
  propCandidates: Array<{ id: string; name: string }>,
  sceneCandidates: Array<{ id: string; name: string }>
): ExtractMentionsResult {
  if (!prompt) {
    return { characterIds: [], propIds: [], sceneId: null, sceneIdsExtra: [] };
  }

  // 收集所有命中（保留位置）后再分类
  type Hit = { index: number; kind: MentionKind; id: string };
  const hits: Hit[] = [];
  const byNameToHits = new Map<
    string,
    Array<{ kind: MentionKind; id: string }>
  >();
  for (const c of characterCandidates) {
    if (!byNameToHits.has(c.name)) byNameToHits.set(c.name, []);
    byNameToHits.get(c.name)!.push({ kind: 'character', id: c.id });
  }
  for (const p of propCandidates) {
    if (!byNameToHits.has(p.name)) byNameToHits.set(p.name, []);
    byNameToHits.get(p.name)!.push({ kind: 'prop', id: p.id });
  }
  for (const s of sceneCandidates) {
    if (!byNameToHits.has(s.name)) byNameToHits.set(s.name, []);
    byNameToHits.get(s.name)!.push({ kind: 'scene', id: s.id });
  }

  // 解析优先级：character > prop > scene
  const PRIORITY: MentionKind[] = ['character', 'prop', 'scene'];
  function pickByPriority(name: string): { kind: MentionKind; id: string } | null {
    const list = byNameToHits.get(name);
    if (!list || list.length === 0) return null;
    for (const k of PRIORITY) {
      const found = list.find((x) => x.kind === k);
      if (found) return found;
    }
    return null;
  }

  // 1) @[名字] 形式
  const occupied: Array<[number, number]> = [];
  const bracketRe = /@\[([^\]]+)\]/g;
  let bm: RegExpExecArray | null;
  while ((bm = bracketRe.exec(prompt)) !== null) {
    occupied.push([bm.index, bm.index + bm[0].length]);
    const picked = pickByPriority(bm[1]);
    if (picked) hits.push({ index: bm.index, ...picked });
  }
  const isOccupied = (idx: number) =>
    occupied.some(([s, e]) => idx >= s && idx < e);

  // 2) 裸 @名字（合法字符集）—— 按所有候选名最长优先匹配
  const allNamesByLen = Array.from(byNameToHits.keys()).sort(
    (a, b) => b.length - a.length
  );
  const bareRe = /@([\u4e00-\u9fa5A-Za-z0-9_]+)/g;
  let bareM: RegExpExecArray | null;
  while ((bareM = bareRe.exec(prompt)) !== null) {
    if (isOccupied(bareM.index)) continue;
    const tail = bareM[1];
    let matched: string | null = null;
    for (const n of allNamesByLen) {
      // 仅当合法字符名时才走"前缀匹配"；非合法字符名只能走 @[名字]
      if (!/^[\u4e00-\u9fa5A-Za-z0-9_]+$/.test(n)) continue;
      if (tail.startsWith(n)) {
        matched = n;
        break;
      }
    }
    if (matched) {
      const picked = pickByPriority(matched);
      if (picked) hits.push({ index: bareM.index, ...picked });
    }
  }

  // 3) 兜底：候选名含空格 / 标点的情况，扫 @<完整名>
  for (const name of allNamesByLen) {
    if (/^[\u4e00-\u9fa5A-Za-z0-9_]+$/.test(name)) continue; // 合法字符名已在第 2 步处理
    const needle = `@${name}`;
    let from = 0;
    while (true) {
      const idx = prompt.indexOf(needle, from);
      if (idx < 0) break;
      if (!isOccupied(idx)) {
        const picked = pickByPriority(name);
        if (picked) hits.push({ index: idx, ...picked });
        break;
      }
      from = idx + 1;
    }
  }

  hits.sort((a, b) => a.index - b.index);

  // 分类去重
  const characterIds: string[] = [];
  const propIds: string[] = [];
  let sceneId: string | null = null;
  const sceneIdsExtra: string[] = [];
  const seen = new Set<string>();

  for (const h of hits) {
    if (seen.has(`${h.kind}:${h.id}`)) continue;
    seen.add(`${h.kind}:${h.id}`);
    if (h.kind === 'character') characterIds.push(h.id);
    else if (h.kind === 'prop') propIds.push(h.id);
    else {
      if (sceneId === null) sceneId = h.id;
      else if (h.id !== sceneId) sceneIdsExtra.push(h.id);
    }
  }

  return { characterIds, propIds, sceneId, sceneIdsExtra };
}
